import { InputText, InputValidation, Modal, Option, Select, showToast, Text } from "@jobber/components";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Button } from "@jobber/components/Button";
import { trpc } from "@/services/trpc";
import { Loader } from "@googlemaps/js-api-loader";
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { CreateServiceAreaUseMutationType, TechniciansListUseQueryType, UpdateServiceAreaUseMutationType } from "@/services/trpc/types";
type Props = {
  refetchList?: () => void
}

type RefFields = {
  openModal: (area?: any) => void
}

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  technicianExternalId: z.string().min(1, "Technician is required"),
  address: z.string().min(1, "Address is required"),
  latitude: z.number().min(-90, "Latitude must be between -90 and 90").max(90, "Latitude must be between -90 and 90"),
  longitude: z.number().min(-180, "Longitude must be between -180 and 180").max(180, "Longitude must be between -180 and 180"),
  geoJson: z.string().nullable().optional()
})

type FormData = z.infer<typeof formSchema>

const CreateServiceAreaModal = forwardRef(({refetchList}: Props, ref: React.Ref<RefFields>) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [id, setId] = useState<string | null>(null)
  const [areaData, setAreaData] = useState<any>(null)

  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [validationBusy, setValidationBusy] = useState(false)

  const techniciansQuery = trpc.technicians.list.useQuery() as TechniciansListUseQueryType
  const createServiceAreaMutation = trpc.serviceAreas.create.useMutation() as CreateServiceAreaUseMutationType
  const updateServiceAreaMutation = trpc.serviceAreas.update.useMutation() as UpdateServiceAreaUseMutationType

  const searchRef = useRef<HTMLInputElement>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      technicianExternalId: '',
      address: '',
      latitude: undefined,
      longitude: undefined,
      geoJson: '',
    }
  })

  const loading = useCallback(() => {
    return false
  }, [])
  useEffect(() => {
    const _initAutoComplete = () => {
      if (searchRef.current && modalOpen) {
        if (form.getValues('address')) {
          searchRef.current.value = form.getValues('address')
        }
        const autocomplete = new window.google.maps.places.Autocomplete(searchRef.current, {
          fields: ['geometry', 'address_components', 'formatted_address'], // Ensures relevant data is returned
          bounds: new window.google.maps.LatLngBounds(new window.google.maps.LatLng(26.5537, -97.3340), new window.google.maps.LatLng(42.9634, -81.7462)),
          types: ['address'],  // Filters to only show addresses
          componentRestrictions: { country: 'us' } // Optionally restrict to a country
        })
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (place.geometry && place.geometry.location) {
            form.setValue('latitude', place.geometry.location.lat())
            form.setValue('longitude', place.geometry.location.lng())
            form.setValue('address', place?.formatted_address || '')
          } else {
            console.error('Place has no geometry');
          }
        });
      }
    }

    if (window?.google?.maps?.places?.Autocomplete) {
      _initAutoComplete()
    } else {
      const loader = new Loader({
        apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
        libraries: ['places', 'marker'],
        version: 'weekly',
      })
      loader.load()
        .then(() => {
          _initAutoComplete()
        })
    }
  }, [searchRef, modalOpen])

  useEffect(() => {
    if (!modalOpen) {
      setId(null)
      setAreaData(null)
      form.reset()
    }
  }, [modalOpen])

  useImperativeHandle(ref,() => ({
    openModal(area?: any) {
      if (area) {
        setId(area.id)
        setAreaData(area)
        form.setValue('name', area.name)
        form.setValue('address', area.address)
        form.setValue('latitude', area.latitude)
        form.setValue('longitude', area.longitude)
        form.setValue('technicianExternalId', area.technicianExternalId)
        form.setValue('geoJson', area.geoJson)
      }
      setModalOpen(true)
    }
  }))

  const getGeoJsonLink = useCallback(() => {
    let url = `https://geojson.io/`
    if (form.watch('geoJson')) url+= `#data=data:application/json,${encodeURIComponent(form.watch('geoJson') as string)}`
    else if (form.watch('longitude') && form.watch('latitude')) url+= `#map=11/${form.watch('latitude')}/${form.watch('longitude')}`
    return url
  }, [form.watch('geoJson'), form.watch('latitude'), form.watch('longitude')])

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    setHasSubmitted(true)
    if (id) {
      updateServiceAreaMutation.mutate({
        id,
        data: values
      },{
        onSuccess: () => {
          setModalOpen(false)
          refetchList && refetchList()
        },
        onError: (e) => {
          console.log(e.message)
          showToast({
            message: e.message,
            variation: 'error'
          })
        }
      })
    } else {
      createServiceAreaMutation.mutate(values, {
        onSuccess: () => {
          setModalOpen(false)
          refetchList && refetchList()
        },
        onError: (e) => {
          console.log(e.message)
          showToast({
            message: e.message,
            variation: 'error'
          })
        }
      })
    }
  }
  return (
    <Modal
      title={id ? `Edit "${areaData.name}" Service Area` : "Create new Service Area"}
      open={modalOpen}
      onRequestClose={() => setModalOpen(false)}
    >
      <form
        style={{ padding: '10px 20px', display: 'flex', flexDirection: 'column', gap: '20px' }}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div>
          <InputText
            placeholder={"Service area name"}
            value={form.watch('name')}
            onChange={(val) => form.setValue('name', val as string)}
          />
          {form.formState.errors.name && <InputValidation message={form.formState.errors.name.message as string} />}
        </div>
        <div>
        <Select
          value={form.watch('technicianExternalId')}
          placeholder="Select Technician"
          defaultValue={form.watch('technicianExternalId')}
          onChange={(val) => form.setValue('technicianExternalId', val as string)}
        >
          <Option value=""></Option>
          { techniciansQuery?.data?.map((t) => (
              <Option key={t.id} value={t.externalId}>{ t.fullName }</Option>
            ))}
        </Select>
        {form.formState.errors.technicianExternalId && <InputValidation message={form.formState.errors.technicianExternalId.message as string} />}
        </div>
        <div>
          <input
            ref={searchRef}
            style={{
              boxSizing: "border-box",
              border: "1px solid #ccc",
              borderRadius: "8px",
              width: "100%",
              height: "50px",
              padding: "10px",
            }}
            type="text"
            placeholder="Enter Address"
          />
          {form.formState.errors.address && <InputValidation message={form.formState.errors.address.message as string} />}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{width: '100%'}}>
            <InputText value={form.watch('latitude')} name={"latitude"} placeholder={"Latitude"} disabled readonly/>
            {form.formState.errors.latitude && <InputValidation message={form.formState.errors.latitude.message as string} />}
          </div>
          <div style={{width: '100%'}}>
            <InputText value={form.watch('longitude')} name={"longitude"} placeholder={"Longitude"} disabled readonly/>
            {form.formState.errors.longitude && <InputValidation message={form.formState.errors.longitude.message as string} />}
          </div>
        </div>
        <div>
          <div style={{ fontFamily: 'monospace' }}>
            <InputText
              placeholder={"GeoJSON"}
              name={"geoJson"}
              multiline
              rows={{ min: 5, max: 10 }}
              value={form.watch('geoJson') || ''}
              onChange={(val) => form.setValue('geoJson', val as string)}
            />
          </div>
          <div>You can <a rel="noreferrer" target="_blank" href={getGeoJsonLink()}>edit this area visually at geojson.io.</a><br/>After making changes there, copy the JSON and paste it back here to save.</div>
          {form.formState.errors.geoJson && <InputValidation message={form.formState.errors.geoJson.message as string} />}
        </div>
        <div style={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            submit
            variation="learning"
            label={id ? 'Save' : 'Create'}
            loading={false}
            disabled={false}
          />
        </div>
      </form>
    </Modal>
)
})

export default CreateServiceAreaModal

export type { RefFields }
